import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'

import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react'

import { FileInput, Textarea } from '@/components/Form'
import { OfferDocumentTypes } from '@/features/offers/constants'
import useOfferGCPR from '@/features/offers/hooks/useOfferGCPR'
import useOfferBookingApproval from '@/features/offers/hooks/useOfferBookingApproval'
import useOfferPRApproval from '@/features/offers/hooks/useOfferPRApproval'
import useOfferQuotationApproval from '@/features/offers/hooks/useOfferQuotationApproval'
import { APPROVAL_TABLE_TYPE } from '@/features/offers/offer-detail/helper/approverTableColumn.helper'
import { hideModal } from '@/stores/ui/modalSlice'
import { toast } from 'react-toastify'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'

const ReplyInvitationModal = ({
  offerId,
  costBudgetIndex,
  vendorSelectionIndex,
  submissionIndex,
  approverIndex,
  inviteeIndex,
  inviterName,
  tableType,
}) => {
  const { useReplyPRInvitation } = useOfferPRApproval()
  const { useReplyBookingInvitation } = useOfferBookingApproval()
  const { useReplyQuotationInvitation } = useOfferQuotationApproval()
  const { useReplyGCPRInvitation } = useOfferGCPR()
  const replyPRInvitation = useReplyPRInvitation()
  const replyBookingInvitation = useReplyBookingInvitation()
  const replyQuotationInvitation = useReplyQuotationInvitation()
  const replyGCPRInvitation = useReplyGCPRInvitation()
  const currentTableType = tableType ?? APPROVAL_TABLE_TYPE.PR_APPROVAL

  const methods = useForm()
  const dispatch = useDispatch()
  const headerTitle = inviterName ? `Reply to (${inviterName})` : 'Reply to (Inviter)'

  const closeModal = () => {
    dispatch(hideModal())
  }

  const onSubmit = async ({ remark, files }) => {
    if (!remark || remark === '') {
      toast.error('Please enter a remark!')
      return
    }

    const normalizedFiles = Array.isArray(files)
      ? files.map((file) => file?._id || file).filter((id) => id)
      : files?._id
        ? [files._id]
        : files
          ? [files]
          : []

    try {
      dispatch(showLoadingOverlay())

      switch (currentTableType) {
        case APPROVAL_TABLE_TYPE.PR_APPROVAL:
          await replyPRInvitation.mutateAsync({
            offerId,
            costBudgetIndex,
            vendorSelectionIndex,
            submissionIndex,
            approverIndex: approverIndex,
            inviteeIndex,
            remark,
            files: normalizedFiles,
          })
          break
        case APPROVAL_TABLE_TYPE.BOOKING_APPROVAL:
          await replyBookingInvitation.mutateAsync({
            offerId,
            approverIndex,
            remark,
            files: normalizedFiles,
          })
          break
        case APPROVAL_TABLE_TYPE.QUOTATION_APPROVAL:
          await replyQuotationInvitation.mutateAsync({
            offerId,
            approverIndex,
            remark,
            files: normalizedFiles,
          })
          break
        case APPROVAL_TABLE_TYPE.GCPR_APPROVAL:
          await replyGCPRInvitation.mutateAsync({
            offerId,
            submissionIndex,
            approverIndex,
            inviteeIndex,
            remark,
            files: normalizedFiles,
          })
          break
        default:
          throw new Error('Unsupported table type for invitation reply')
      }

      methods.reset({ remark: '', files: [] })
      closeModal()
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  return (
    <Modal isOpen={true} onClose={closeModal}>
      <ModalOverlay />
      <ModalContent>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ModalHeader>{headerTitle}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Textarea name="remark" label="Your Reply" row="5" />
              <FileInput
                label={'Attachment'}
                name="files"
                fileType={OfferDocumentTypes.SOURCING_INTERNAL_DOCUMENT}
                isRow
                canUpload
                isMultiple
                isRequired={false}
              />
            </ModalBody>
            <ModalFooter>
              <HStack>
                <Button
                  colorScheme="green"
                  type="submit"
                  isLoading={
                    replyPRInvitation.isLoading ||
                    replyBookingInvitation.isLoading ||
                    replyQuotationInvitation.isLoading ||
                    replyGCPRInvitation.isLoading
                  }
                >
                  Submit Reply
                </Button>
                <Button colorScheme="gray" onClick={closeModal}>
                  Close
                </Button>
              </HStack>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  )
}

export default ReplyInvitationModal
